import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../services/http.js';
import { ErrorRetryCard } from '../../components/common/ErrorRetryCard.jsx';

const STATUS_FILTERS = [
  { id: 'pending', label: 'Pendentes' },
  { id: 'reviewed', label: 'Revisadas' },
  { id: 'dismissed', label: 'Descartadas' },
];

const REASON_LABELS = {
  doutrina_incorreta: 'Doutrina incorreta',
  ofensivo: 'Ofensivo',
  conteudo_perigoso: 'Conteúdo perigoso',
  outro: 'Outro',
};

const PAGE_SIZE = 20;

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).replace('T', ' ').slice(0, 16);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '');
}

// Aba "Denúncias" do painel admin (Guideline 1.2 da Apple exige um processo de
// moderação, não só o botão de denúncia no chat).
export default function ContentReportsTab() {
  const [status, setStatus] = useState('pending');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { res, data } = await apiClient.getAdminContentReports({ status, limit: PAGE_SIZE, offset });
      if (!res.ok) {
        setError(apiClient.parseApiError(data, 'Não foi possível carregar as denúncias.'));
        return;
      }
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch {
      setError('Falha de conexão ao carregar as denúncias.');
    } finally {
      setLoading(false);
    }
  }, [status, offset]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = (next) => { setOffset(0); setStatus(next); };

  const moderate = async (reportId, nextStatus) => {
    setBusyId(reportId);
    try {
      const { res, data } = await apiClient.updateAdminContentReport({ reportId, status: nextStatus });
      if (!res.ok) {
        setError(apiClient.parseApiError(data, 'Não foi possível atualizar a denúncia.'));
        return;
      }
      await load();
    } catch {
      setError('Falha de conexão ao atualizar a denúncia.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2 text-on-surface">
            <span className="material-symbols-outlined">flag</span> Denúncias de conteúdo
          </h2>
          <p className="text-sm text-on-surface-variant mt-1">Respostas da IA denunciadas pelos usuários.</p>
        </div>
        <div className="flex gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => changeStatus(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                status === f.id
                  ? 'bg-primary text-on-primary'
                  : 'bg-surface-container-highest text-on-surface hover:bg-surface-variant'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorRetryCard message={error} onRetry={load} />}

      {loading ? (
        <div className="flex items-center justify-center h-32 text-sm text-on-surface-variant gap-2">
          <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
          Carregando denúncias...
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-on-surface-variant">Nenhuma denúncia nesta categoria.</p>
      ) : (
        <ul className="space-y-4">
          {items.map((r) => (
            <li key={r.id} className="rounded-xl border border-outline-variant/20 bg-surface-container-low p-4 space-y-3">
              <div className="flex flex-wrap justify-between gap-2 text-xs text-on-surface-variant">
                <span className="font-bold text-on-surface">{REASON_LABELS[r.reason] || r.reason}</span>
                <span>{fmtDateTime(r.created_at)} · {r.reporter_email || 'usuário removido'}</span>
              </div>
              {r.comment && (
                <p className="text-sm text-on-surface">
                  <span className="text-on-surface-variant">Comentário: </span>{r.comment}
                </p>
              )}
              <pre className="whitespace-pre-wrap break-words text-xs bg-surface-container-highest rounded-lg p-3 max-h-60 overflow-auto font-body">{r.message_snapshot}</pre>
              {status === 'pending' && (
                <div className="flex gap-2 justify-end">
                  <button
                    disabled={busyId === r.id}
                    onClick={() => moderate(r.id, 'dismissed')}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-container-highest text-on-surface hover:bg-surface-variant disabled:opacity-50"
                  >
                    Descartar
                  </button>
                  <button
                    disabled={busyId === r.id}
                    onClick={() => moderate(r.id, 'reviewed')}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary text-on-primary disabled:opacity-50"
                  >
                    Marcar como revisada
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className="flex justify-between items-center text-xs text-on-surface-variant">
          <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} de {total}</span>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="px-3 py-1.5 rounded-lg bg-surface-container-highest disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="px-3 py-1.5 rounded-lg bg-surface-container-highest disabled:opacity-40"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
